import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchIcon from "@mui/icons-material/Search";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import { Bar, BarChart, CartesianGrid, Label, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import ExecutiveOverview from "./components/ExecutiveOverview";
import PaymentPlatformExecutiveTable from "./components/PaymentPlatformExecutiveTable";
import {
  getPaymentPlatformOverview,
  searchPaymentPlatformEntities,
} from "./api/companyApi";

const fmt = (value) => Number(value || 0).toLocaleString();
function PaymentPlatformPriorityActions({ tenantId, rows, totals }) {
  const navigate = useNavigate();
  const reviewCount = Number(totals.review || 0);
  const highRiskCount = Number(totals.high || 0);
  const filters = [
    ["all", "All tasks", reviewCount],
    ["new", "New companies", 0],
    ["ai", "AI Recommended", reviewCount],
    ["risk", "Riskiest companies", highRiskCount],
    ["transferred", "Tasks transferred to you", 0],
  ];
  const [filter, setFilter] = useState("all");
  const visibleRows = filter === "risk"
    ? rows.filter((row) => String(row?.assessment?.recommendation?.riskBand || "").toLowerCase() === "high")
    : ["new", "transferred"].includes(filter) ? [] : rows;

  return (
    <Paper
      data-payment-platform-priority-actions
      variant="outlined"
      sx={{ mb: 3, borderRadius: "18px", overflow: "hidden", borderColor: "#e4e9f2", boxShadow: "0 12px 30px rgba(35,54,91,.06)" }}
    >
      <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2.5, pb: 1.75, bgcolor: "#fff" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: .75 }}>
              <Typography sx={{ color: "#17233f", fontWeight: 700, fontSize: 20, letterSpacing: "-.015em" }}>
                Priority actions
              </Typography>
              <Tooltip title="The Liberis assessments that require the most immediate review." arrow>
                <HelpOutlineIcon sx={{ color: "#718096", fontSize: 18 }} />
              </Tooltip>
            </Box>
            <Typography variant="body2" sx={{ color: "#6c7890", mt: .25 }}>
              Your highest-impact open decisions and next steps.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Button startIcon={<AddBusinessIcon />} variant="contained" size="small" onClick={() => navigate(`/tenant-dashboard/${tenantId}/assessments?addCompany=1`)} sx={{ borderRadius: "9px", textTransform: "none", fontWeight: 700 }}>+ Add company</Button>
            <Button startIcon={<AccountBalanceOutlinedIcon />} variant="outlined" size="small" onClick={() => navigate(`/tenant-dashboard/${tenantId}/assessments`)} sx={{ borderColor: "#d9e1ee", color: "#40506e", borderRadius: "9px", textTransform: "none", fontWeight: 650 }}>Open assessments</Button>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
          {filters.map(([key, label, count]) => (
            <Chip
              key={key}
              clickable
              onClick={() => setFilter(key)}
              label={`${label}  ${fmt(count)}`}
              color={filter === key ? "primary" : "default"}
              variant={filter === key ? "filled" : "outlined"}
              sx={{ borderRadius: "8px", fontWeight: 600, bgcolor: filter === key ? "#3168e0" : "#fff", borderColor: "#dfe5ef" }}
            />
          ))}
        </Box>
      </Box>
      <PaymentPlatformExecutiveTable
        rows={visibleRows}
        onOpenCompany={(entityId) => navigate(`/tenant-dashboard/${tenantId}/assessments/company/${entityId}`)}
        viewport="priority-actions"
        emptyText="No open tasks in this view."
      />
    </Paper>
  );
}

function PaymentPlatformRiskChart({ risks }) {
  const data = [
    { range: "Low", value: Number(risks.low || 0) },
    { range: "Medium", value: Number(risks.medium || 0) },
    { range: "High", value: Number(risks.high || 0) },
    { range: "Unscored", value: Number(risks.unscored || 0) },
  ];

  return (
    <Card data-payment-platform-risk-chart variant="outlined" sx={{ height: "fit-content", mt: "15px", pb: "10px", boxShadow: "inset 0px 1px 3px #0000001A, 0px 3px 6px #00000029", bgcolor: "#FAFAFC" }}>
      <Typography variant="h5" sx={{ ml: "30px", color: "#1A2A56", fontSize: "26px", fontWeight: 500, mt: 3 }}>
        Customer&apos;s Risk
      </Typography>
      <Box sx={{ height: 340, pt: 2.5, pb: 1, overflowX: "auto", display: "flex", justifyContent: "center" }}>
          <BarChart width={800} height={300} data={data} margin={{ top: 5, right: 30, left: 50, bottom: 0 }}>
            <CartesianGrid vertical={false} strokeDasharray="1 0" />
            <XAxis dataKey="range" axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} allowDecimals={false}>
              <Label value="Number of Customers" angle={-90} position="insideLeft" style={{ textAnchor: "middle", fill: "#1A2A56", fontWeight: 400 }} offset={-30} />
            </YAxis>
            <ChartTooltip formatter={(value) => fmt(value)} />
            <Bar dataKey="value" fill="#5186EC" barSize={52} radius={[2, 2, 0, 0]} />
          </BarChart>
      </Box>
    </Card>
  );
}

function PaymentPlatformPortfolioTable({ tenantId }) {
  const navigate = useNavigate();
  const [result, setResult] = useState({ items: [], nextCursor: null, total: null });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [decision, setDecision] = useState("");
  const [risk, setRisk] = useState("");
  const [coverage, setCoverage] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);
  const [cursors, setCursors] = useState([null]);
  const [error, setError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(0);
    setCursors([null]);
  }, [debouncedSearch, decision, risk, coverage, pageSize]);

  useEffect(() => {
    if (!tenantId) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    searchPaymentPlatformEntities(tenantId, {
      query: debouncedSearch || undefined,
      decision: decision || undefined,
      risk: risk || undefined,
      minCoverage: coverage || undefined,
      pageSize,
      cursor: cursors[page] || undefined,
    })
      .then((data) => {
        if (active) setResult(data || { items: [], nextCursor: null, total: null });
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message || "Could not load the portfolio table.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [tenantId, debouncedSearch, decision, risk, coverage, pageSize, page, cursors]);

  const nextPage = () => {
    if (!result.nextCursor) return;
    setCursors((current) => {
      const next = current.slice(0, page + 1);
      next[page + 1] = result.nextCursor;
      return next;
    });
    setPage((current) => current + 1);
  };

  const openCompany = (entityId) =>
    navigate(`/tenant-dashboard/${tenantId}/assessments/company/${entityId}`);

  return (
    <Paper
      data-payment-platform-portfolio-table
      variant="outlined"
      sx={{ mt: 3, mb: 3, borderRadius: "18px", overflow: "hidden", borderColor: "#e4e9f2", boxShadow: "0 12px 30px rgba(35,54,91,.06)" }}
    >
      <Box sx={{ px: { xs: 2, md: 2.75 }, pt: 2.5, pb: 2, bgcolor: "#fff" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ color: "#17233f", fontWeight: 700, fontSize: 20, letterSpacing: "-.015em" }}>Portfolio assessments</Typography>
          <Button startIcon={<AddBusinessIcon />} variant="contained" size="small" onClick={() => navigate(`/tenant-dashboard/${tenantId}/assessments?addCompany=1`)} sx={{ borderRadius: "9px", textTransform: "none", fontWeight: 700 }}>+ Add company</Button>
        </Box>
        <Typography variant="body2" sx={{ color: "#6c7890", mt: .25, mb: 2 }}>
          Every Liberis merchant assessment, with full search, filtering and company reports.
        </Typography>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, merchant ID, EIN, email, website or application…"
            sx={{ flex: "1 1 390px" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          />
          <TextField select size="small" label="Decision" value={decision} onChange={(event) => setDecision(event.target.value)} sx={{ minWidth: 145 }}>
            <MenuItem value="">All decisions</MenuItem>
            <MenuItem value="approve">Approve</MenuItem>
            <MenuItem value="review">Review</MenuItem>
            <MenuItem value="decline">Decline</MenuItem>
            <MenuItem value="insufficient_data">Insufficient data</MenuItem>
          </TextField>
          <TextField select size="small" label="Risk" value={risk} onChange={(event) => setRisk(event.target.value)} sx={{ minWidth: 125 }}>
            <MenuItem value="">All risk</MenuItem>
            <MenuItem value="low">Low</MenuItem>
            <MenuItem value="medium">Medium</MenuItem>
            <MenuItem value="high">High</MenuItem>
          </TextField>
          <TextField select size="small" label="Evidence" value={coverage} onChange={(event) => setCoverage(event.target.value)} sx={{ minWidth: 145 }}>
            <MenuItem value="">Any coverage</MenuItem>
            <MenuItem value="25">25%+</MenuItem>
            <MenuItem value="50">50%+</MenuItem>
            <MenuItem value="75">75%+</MenuItem>
          </TextField>
        </Box>
      </Box>
      {error ? (
        <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
      ) : loading ? (
        <Box sx={{ py: 7, textAlign: "center" }}><CircularProgress size={30} /></Box>
      ) : (
        <PaymentPlatformExecutiveTable
          rows={result.items || []}
          onOpenCompany={openCompany}
          viewport="portfolio-assessments"
        />
      )}
      <Box sx={{ px: { xs: 2, md: 2.75 }, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", borderTop: "1px solid #edf0f5" }}>
        <Typography sx={{ color: "#6c7890", fontSize: 13 }}>
          Page {page + 1}{result.total != null ? ` · ${fmt(result.total)} companies` : ""}{result.searchLimited ? " · first 1,000 search matches" : ""}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField select size="small" label="Rows" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))} sx={{ width: 90 }}>
            <MenuItem value={25}>25</MenuItem><MenuItem value={50}>50</MenuItem><MenuItem value={100}>100</MenuItem>
          </TextField>
          <Button variant="outlined" disabled={page === 0 || loading} onClick={() => setPage((current) => Math.max(0, current - 1))}>Previous</Button>
          <Button variant="contained" disabled={!result.nextCursor || loading} onClick={nextPage}>Next</Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default function PaymentPlatformDashboardPage() {
  const navigate = useNavigate();
  const { tenantIdp } = useParams();
  const [overview, setOverview] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!tenantIdp) return;
    setLoading(true);
    setError("");
    try {
      const [overviewData, reviewData] = await Promise.all([
        getPaymentPlatformOverview(tenantIdp),
        searchPaymentPlatformEntities(tenantIdp, { decision: "review", pageSize: 10 }),
      ]);
      setOverview(overviewData);
      setReviewItems(reviewData?.items || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || "Could not load the payment-platform dashboard.");
    } finally {
      setLoading(false);
    }
  }, [tenantIdp]);

  useEffect(() => { load(); }, [load]);

  const job = overview?.latestImport;
  const decisions = job?.decisionDistribution || {};
  const risks = job?.riskDistribution || {};
  const matched = Number(job?.matchedEntities || 0);
  const assessed = Number(job?.assessedEntities || 0);
  const riskTotal = Number(risks.low || 0) + Number(risks.medium || 0) + Number(risks.high || 0);
  const portfolioScore = riskTotal
    ? (Number(risks.low || 0) * 2.5 + Number(risks.medium || 0) * 6 + Number(risks.high || 0) * 8.5) / riskTotal
    : 0;
  const metricCards = useMemo(() => [
    {
      eyebrow: "Assessed merchants",
      value: fmt(assessed || matched),
      detail: `${fmt(job?.unmatchedRows)} merchants require additional identity review.`,
      accent: "#3168e0",
      icon: <BusinessRoundedIcon />,
      action: "Open all assessments",
      onClick: () => navigate(`/tenant-dashboard/${tenantIdp}/assessments`),
    },
    {
      eyebrow: "Decision queue",
      value: fmt(decisions.review),
      detail: `${fmt(decisions.decline)} declined · ${fmt(risks.high)} high-risk merchants.`,
      accent: "#d79a27",
      icon: <ScheduleRoundedIcon />,
      action: "Review decisions",
      onClick: () => navigate(`/tenant-dashboard/${tenantIdp}/assessments`),
    },
    {
      eyebrow: "Approved",
      value: fmt(decisions.approve),
      detail: `Current recommendations from model v${overview?.model?.version || 1}.`,
      accent: "#35a477",
      icon: <CheckCircleRoundedIcon />,
      action: "View approved merchants",
      onClick: () => navigate(`/tenant-dashboard/${tenantIdp}/assessments`),
    },
  ], [assessed, matched, job?.unmatchedRows, decisions.review, decisions.decline, decisions.approve, risks.high, overview?.model?.version, navigate, tenantIdp]);

  if (loading) {
    return <Box sx={{ minHeight: 420, display: "grid", placeItems: "center" }}><CircularProgress aria-label="Loading payment-platform dashboard" /></Box>;
  }
  if (error || !overview) return <Alert severity="error">{error || "Dashboard data is unavailable."}</Alert>;

  return (
    <Box data-payment-platform-dashboard>
      <ExecutiveOverview
        isProspects={false}
        portfolioScore={portfolioScore}
        prospectsCount={0}
        approvedLimit={0}
        existingCustomers={matched}
        customersAtRisk={Number(risks.high || 0)}
        moneyAtRisk={0}
        onReviewRisk={() => document.querySelector("[data-payment-platform-risk-chart]")?.scrollIntoView({ behavior: "smooth" })}
        onLoadAging={load}
        scoreLabel="ArcAI portfolio risk score"
        intelligenceLabel="Live MCA / loan intelligence"
        metricCards={metricCards}
      />
      <PaymentPlatformPriorityActions
        tenantId={tenantIdp}
        rows={reviewItems}
        totals={{ review: decisions.review, high: risks.high }}
      />
      <PaymentPlatformRiskChart risks={risks} />
      <PaymentPlatformPortfolioTable tenantId={tenantIdp} />
    </Box>
  );
}
